<template>
    <div :class="[addon||addonSlot||prepend||prependSlot ? 'input-group' : '', 'form-input-text', icon ? 'input-text-icon' : '']">
      <div class="input-group-prepend" v-if="prepend || prependSlot">
        <span class="input-group-addon">
          <span v-if="prepend" v-html="prepend" />
          <slot name="prepend"></slot>
        </span>
      </div>
      <input 
        ref="inputField"
        v-bind:type="type"
        :class="['form-control', variant, hasError() ? 'is-invalid' : '']"
        :width="width"
        v-bind:value="value"
        v-on:input="inputUpdated"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :min="min"
        @keypress="onKeypress"
        @keydown.enter="onEnterPressed"
        @keydown.tab="$emit('tab', $event)"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
        @click="$emit('click', $event)"
        :readonly="readonly" />
      <i :class="['fas', icon, 'input-label-icon']" v-if="icon" />
      <div v-if="addon || addonSlot" class="input-group-append">
        <span class="input-group-addon" v-if="addon" v-html="addon" />
        <slot name="addon"></slot>
      </div>
      <div class="form-invalid-feedback" v-if="hasError()">
        {{getError()}}
      </div>
      <div v-if="!hasError() && note" class="form-field-note">{{note}}</div>
    </div>
</template>

<style>
.form-input-text.input-text-icon {
  position: relative;
}
.form-input-text.input-text-icon input {
  padding-left: 30px !important;
}
.input-label-icon {
  position: absolute;
  top: 12px;
  left: 10px;
  color: #c9cbcc;
}
</style>

<script>
import { mapState } from 'vuex'

import formField from '@/mixins/formField'
import Utils from '@/utils/utils'

export default {
  name: "FormInputAddressSearch",
  mixins: [formField],
  props: {
    type: {
      type: String,
      default: 'text'
    },
    variant: String,
    value: [String, Number],
    placeholder: String,
    field: String,
    note: String,
    addon: String,
    addonSlot: Boolean,
    prepend: String,
    prependSlot: Boolean,
    readonly: Boolean,
    icon: String,
    min: [String, Number],
    countryCode: String,
    wholeNumber: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number,
      default: 20
    },
    maxlength: {
      type: Number,
      default: null
    },
    splitAddressComponents: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      placesAutocomplete: null
    }
  },
  computed: {
    filterCountryCode() {
      if(this.countryCode)
        return this.countryCode

      if(this.$store.state.account.countryCode)
        return this.$store.state.account.countryCode

      // Default to australia if nothing setup
      return 'au'
    }
  },
  watch: {
    value: function(newVal, oldVal) {
      // Reset error when typing if the field was blank
      // Not 100% fix to clearing errors (eg, some errors may be logic based) but helpful
      if(oldVal == '' || oldVal == null)
        this.resetFieldError(this.$props.field)

      // Default to empty string if null given
      if(newVal == null) {
        this.$nextTick(() => {
          this.$emit('input', '')
        })
      }
    },
    readonly: function() {
      if(!this.readonly) {
        this.enableAutocomplete()
      } else {
        this.disableAutocomplete()
      }
    },
    countryCode: function() {
      if(this.placesAutocomplete) {
        this.placesAutocomplete.setComponentRestrictions({ country: this.filterCountryCode } )
      }
    }
  },
  mounted() {
    this.loadAutocomplete()
  },
  beforeDestroy() {
    if(this.placesAutocomplete) {
      this.$mapService.googleMapAPI.maps.event.clearInstanceListeners(this.$refs.inputField)
    }
  },
  methods: {
    loadAutocomplete() {
      if(!this.placesAutocomplete && !this.readonly) {
        this.$mapService.loadGoogleMaps().then(() => {
          if(!this.$mapService.googleMapAPI)
            return

          var options = {
            fields: ["address_components", "name", "formatted_address"],
          }
          options.componentRestrictions = { country: this.filterCountryCode }
            
          this.placesAutocomplete = new this.$mapService.googleMapAPI.maps.places.Autocomplete(this.$refs.inputField, options)
          this.placesAutocomplete.addListener('place_changed', this.onPlaceChanged)
        })
      }
    },
    onPlaceChanged() {
      var placeInfo = this.placesAutocomplete.getPlace()
      if(!placeInfo)
        return
      if(this.readonly)
        return

      let _calculateComponent = (type, fieldType = 'long_name') => {
        var result = ''
        if(placeInfo.address_components) {
          for(let comp of placeInfo.address_components) {
            if(comp.types.includes(type)) {
              result = comp[fieldType]
              break
            }
          }
        }
        return result
      }

      var addressDetails = {
        address1: '',
        address2: '',
        suburb: '',
        state: '',
        postcode: '',
        country: ''
      }
      addressDetails.address1 = (_calculateComponent('street_number') + ' ' + _calculateComponent('route')).trim()

      addressDetails.suburb = _calculateComponent('locality')
      addressDetails.state = _calculateComponent('administrative_area_level_1', 'short_name')
      addressDetails.postcode = _calculateComponent('postal_code')
      addressDetails.country = _calculateComponent('country') || _calculateComponent('administrative_area_level_2')

      if(!this.splitAddressComponents) {
        if(placeInfo.formatted_address) {
          // Format address into our format
          // Used for comparing addresses by text
          var formattedAddr = Utils.buildAddressString(addressDetails.address1, '', addressDetails.suburb, addressDetails.postcode, addressDetails.state, '')

          this.$emit('input', formattedAddr)
          this.$refs.inputField.value = formattedAddr
        }
        return
      }

      this.$nextTick(() => {
        this.$emit('updateAddress2', addressDetails.address2)
        this.$emit('updateSuburb', addressDetails.suburb)
        this.$emit('updateState', addressDetails.state)
        this.$emit('updatePostcode', addressDetails.postcode)
        this.$emit('updateCountry', addressDetails.country)

        this.$emit('input', addressDetails.address1)
        
        // Force set as google autocomplete fills it in too when clicking
        this.$refs.inputField.value = addressDetails.address1
      })
    },
    enableAutocomplete() {
      if(!this.placesAutocomplete)
        this.loadAutocomplete()      
    },
    disableAutocomplete() {
      if(this.placesAutocomplete) {
        this.$mapService.googleMapAPI.maps.event.clearInstanceListeners(this.$refs.inputField)
        this.placesAutocomplete = null
      }
    },
    hasError() {
      return this.getFieldError(this.$props.field).length > 0
    },
    getError() {
      return this.getFieldError(this.$props.field)
    },
    focus() {
      this.$refs.inputField.focus()
    },
    select() {
      this.$refs.inputField.select()
    },
    blur() {
      this.$refs.inputField.blur()
    },
    setSelectionRange(selectionStart, selectionEnd) {
      this.$refs.inputField.setSelectionRange(selectionStart, selectionEnd)
    },
    onEnterPressed(ev) {
      this.$emit('enter', ev)      
    },
    inputUpdated(evt) {
      this.$emit('input', evt.target.value)
    },
    onKeypress(evt) {
      if(this.wholeNumber) {
        // Disallow decimal places for whole numbers
        let keyCode = (evt.keyCode ? evt.keyCode : evt.which)
        if(keyCode === 46)
          return evt.preventDefault()
      }
    },
    insertTextAtCursor(insertText) {
      var startPos = this.$refs.inputField.selectionStart
      var endPos = this.$refs.inputField.selectionEnd
      var cursorPos = startPos
      var curValue = '' + this.value
      
      var updatedContent = curValue.substring(0, startPos) + insertText + curValue.substring(endPos, curValue.length)

      this.$emit('input', updatedContent)

      // Move cursor
      setTimeout(() => {
        cursorPos += insertText.length
        this.$refs.inputField.selectionStart = this.$refs.inputField.selectionEnd = cursorPos
      }, 10)
    }
  }
};
</script>